import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import Pagination from '@/components/Common/Pagination.tsx';
import { Select } from '@/components/Controls/Select';
import { PageRequest, Processing } from '@/api/entities/processing.ts';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import {
  useGetNextDataReport,
  useGetAllProcessing,
  useGetAllComponentKey,
  useGetAllDefectKey,
  useDeleteFirestoreImageByImageUrl,
  useDeleteProcessing,
} from '@/hooks/ai.hooks';
import { mapComponentOptions } from '@/utils/ia.utils';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { PageInfoHeading } from '@/components/Header/Page/PageInfoHeading';
import { HeadingButton } from '@/components/Custom/Button/HeadingButton';
import Card from '@/components/Card';
import { TableTitle } from '@/components/Header/TableTitle';
import { TableHead } from '@/components/Datatable/TableHead';
import { StatusTag } from '@/components/Tag/StatusTag';
import Loader from '@/components/Loading/Loader';
import { TableCell } from '@/components/Datatable/TableCell';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { NoTableDataFound } from '@/components/Custom/NotFound/NoTableDataFound';
import { useLocalStorageState } from '@/hooks/local-storage.hooks';
import { ImportIcon } from '@/components/SvgIcons/ImportIcon';
import { EngineCorrectionUploadModal } from '@/components/Custom/Modal/EngineCorrectionUploadModal';
import { showModal } from '@/utils/modal.utils';
import ButtonAdvancedFilter from '@/components/Custom/Button/ButtonAdvancedFilter';
import { ClearButton } from '@/components/Custom/Button/ClearButton';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import ConfirmationModal from '@/components/Custom/Modal/ConfirmationModal';
import { ImagePreviewModal } from '@/components/Custom/Modal/ImagePreviewModal';
import { twMerge } from 'tailwind-merge';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { useListAllUsers } from '@/hooks/user.hooks';
import { RoleName } from '@/api/entities/user-role-type';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import dayjs from 'dayjs';

const initialPageRequest: PageRequest = {
  page: 1,
  limit: 100,
  moderatorUserId: 'all',
  status: 'all',
  verificationStatus: 'all',
  isManualUpload: 'all',
  key: '',
  defectKey: '',
};

export const EngineCorrectionListPage = () => {
  const { t } = useTranslation('engineCorrection');
  const { loggedInUser } = useAuth();
  const { isAdmin } = useUserRole();
  const { currentPage, setCurrentPage } = useContext(EngineCorrectionContext);
  initialPageRequest.page = currentPage;
  const ID_INIT = '64caef2d9f0e53ad5c5f2c13';
  const [filter, setFilter] = useState<PageRequest>(initialPageRequest);
  const [usedStartDate, setUsedStartDate] = useLocalStorageState({
    key: 'engineCorrection_usedStartDate',
    defaultValue: null,
  });
  const [usedEndDate, setUsedEndDate] = useLocalStorageState({
    key: 'engineCorrection_usedEndDate',
    defaultValue: null,
  });
  const [moderatorUserId, setModeratorUserId] = useLocalStorageState({
    key: 'engineCorrection_moderatorUserId',
    defaultValue: 'all',
  });
  const [moderationStatus, setModerationStatus] = useLocalStorageState({
    key: 'engineCorrection_moderationStatus',
    defaultValue: 'all',
  });
  const [verificationStatus, setVerificationStatus] = useLocalStorageState({
    key: 'engineCorrection_verificationStatus',
    defaultValue: 'all',
  });
  const [isManualUpload, setIsManualUpload] = useLocalStorageState({
    key: 'engineCorrection_uploadedImagesStatus',
    defaultValue: 'all',
  });
  const [componentKey, setComponentKey] = useLocalStorageState({
    key: 'engineCorrection_componentKey',
  });
  const [defectKey, setDefectKey] = useLocalStorageState({
    key: 'engineCorrection_defectKey',
  });
  const filtersEmptyRef = useRef<boolean>(true);
  const { data, isLoading, isFetching } = useGetAllProcessing(filter);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [processings, setProcessings] = useState<Processing[]>([]);
  const [selectedProcessing, setSelectedProcessing] = useState<Processing>();
  const [isUploadingImages, setIsUploadingImages] = useState<boolean>(false);

  const { data: allModerators, isLoading: isLoadingAllModerators } =
    useListAllUsers({
      roleTypeNames: [RoleName.ADMIN, RoleName.MODERATOR],
    });

  let allModeratorsOptions: { value: string; label: string }[] = [];

  if (isAdmin) {
    allModeratorsOptions =
      allModerators?.data?.map((moderator) => ({
        value: moderator?.id || '',
        label: `${moderator?.firstname || ''} ${
          moderator?.lastname?.toUpperCase() || ''
        }`,
      })) ?? [];
  } else {
    allModeratorsOptions =
      allModerators?.data
        ?.filter((moderator) => moderator?.id === loggedInUser?.id)
        .map((moderator) => ({
          value: moderator?.id || '',
          label: t('common:myself'),
        })) ?? [];
  }

  const { data: dataToModerate } = useGetNextDataReport(ID_INIT);
  const { data: keyCars, isLoading: isLoadingKeyCars } =
    useGetAllComponentKey();
  const { data: defectKeyCars, isLoading: isLoadingDefectKeyCars } =
    useGetAllDefectKey();

  const { mutate: deleteProcessing, isPending: isPendingDeleteProcessing } =
    useDeleteProcessing({
      t,
      handleSuccess: () => {
        deleteFirestoreImage(selectedProcessing?.imageUrl);
      },
    });

  const {
    mutate: deleteFirestoreImage,
    isPending: isPendingDeleteFirestoreImage,
  } = useDeleteFirestoreImageByImageUrl({
    t,
    handleSuccess: () => {
      setSelectedProcessing(undefined);
    },
  });

  useEffect(() => {
    // Clear the current data immediately when filters change to prevent showing stale data
    setProcessings([]);
    setTotalPages(0);

    // Reset page to 1 when filters change
    setCurrentPage(1);

    setFilter((prevFilter) => ({
      ...prevFilter,
      page: 1, // Always reset to page 1 when filters change
      usedStartDate: usedStartDate ?? undefined,
      usedEndDate: usedEndDate ?? undefined,
      moderatorUserId: moderatorUserId,
      status: moderationStatus,
      verificationStatus: verificationStatus,
      isManualUpload: isManualUpload,
      key: componentKey,
      defectKey: defectKey,
    }));

    filtersEmptyRef.current =
      moderatorUserId === 'all' &&
      moderationStatus === 'all' &&
      verificationStatus === 'all' &&
      isManualUpload === 'all' &&
      !componentKey &&
      !defectKey &&
      !usedStartDate &&
      !usedEndDate;
  }, [
    moderatorUserId,
    moderationStatus,
    verificationStatus,
    isManualUpload,
    componentKey,
    defectKey,
    usedStartDate,
    usedEndDate,
    setCurrentPage,
  ]);

  const isLoadingAll =
    isLoading ||
    isFetching ||
    isLoadingKeyCars ||
    isLoadingDefectKeyCars ||
    isLoadingAllModerators ||
    isUploadingImages ||
    isPendingDeleteProcessing ||
    isPendingDeleteFirestoreImage;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setFilter({
      ...filter,
      page,
    });
  };

  useEffect(() => {
    setProcessings(data?.data?.data ?? []);
    setTotalPages(data?.data?.totalPages ?? 0);
  }, [data]);

  const searchUsedStartDate = (value: dayjs.Dayjs) => {
    setUsedStartDate(value.format('YYYY-MM-DD'));
  };

  const searchUsedEndDate = (value: dayjs.Dayjs) => {
    setUsedEndDate(value.format('YYYY-MM-DD'));
  };

  const filterByModeratorUserId = (value: string) => {
    setModeratorUserId(value);
  };

  const filterByModerationStatus = (value: string) => {
    setModerationStatus(value);
  };

  const filterByVerificationStatus = (verificationStatusValue: string) => {
    setVerificationStatus(verificationStatusValue);
  };

  const filterByUploadedImagesStatus = (uploadedImagesStatusValue: string) => {
    setIsManualUpload(uploadedImagesStatusValue);
  };

  const filterByLimit = (limit: number) => {
    setFilter({
      ...filter,
      limit,
    });
  };

  const filterByComponentKey = (componentKeyValue: string) => {
    setComponentKey(componentKeyValue);
  };

  const filterByDefectKey = (defectKeyValue: string) => {
    setDefectKey(defectKeyValue);
  };

  const clearFilters = () => {
    setUsedStartDate(null);
    setUsedEndDate(null);
    setModeratorUserId('all');
    setModerationStatus('all');
    setVerificationStatus('all');
    setIsManualUpload('all');
    setComponentKey('');
    setDefectKey('');
    setCurrentPage(1);
  };

  const onClickToModerate = useCallback(() => {
    window.location.href = `/engine-correction/${dataToModerate?.data.id}`;
  }, [dataToModerate]);

  const onUploadingImages = (state: boolean) => {
    setIsUploadingImages(state);
  };

  const handleDeleteImage = () => {
    deleteProcessing(selectedProcessing?.id);
  };

  return (
    <>
      <PageHeader
        title={t('engineCorrectionElements')}
        totalItems={data?.data?.total}
        headingElement={
          <PageInfoHeading text={t('common:detailsRelatedToCurrentSection')} />
        }
        actionListElement={
          <>
            <HeadingButton
              buttonClassName="border-purple-5433FF/45 bg-white"
              textClassName="text-primary"
              text={t('common:uploadImages')}
              leftIcon={
                <ImportIcon className="-mt-[1.5px] w-[15.5px] h-[17px] [&>path]:fill-primary" />
              }
              disabled={isLoadingAll}
              onClick={() => {
                showModal('#engine-correction-upload-modal');
              }}
            />
            <HeadingButton
              buttonClassName="border-purple-5433FF/45 bg-blue-50"
              textClassName="text-primary"
              text={t('startModeration')}
              leftIcon={
                <i className="-mt-[1.5px] ki-outline ki-size !text-[18px] text-primary font-bold" />
              }
              disabled={isLoadingAll || !dataToModerate?.data?.id}
              onClick={onClickToModerate}
            />
          </>
        }
      />
      <Card bodyClassName="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-5">
          <div className="flex flex-1">
            <TableTitle text={t('elementList')} />
          </div>
          <div className="flex flex-wrap justify-end gap-x-5 gap-y-3">
            <div className="flex gap-5">
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(keyCars?.data, '1', t)}
                onChange={(e) => filterByComponentKey(e.target.value)}
                value={componentKey}
              />
              <Select
                selectClassName="h-[36px] w-max max-w-[280px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                options={mapComponentOptions(defectKeyCars?.data, '2', t)}
                onChange={(e) => filterByDefectKey(e.target.value)}
                value={defectKey}
              />
            </div>
            <div>
              <ButtonAdvancedFilter>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-row gap-3 select-datepicker-container">
                    <div className="flex flex-row gap-3">
                      <label className="text-gray-600 text-sm self-center">
                        {t('common:modifyBetween')}
                      </label>
                      <DateTimePicker
                        useTime={false}
                        value={usedStartDate ? dayjs(usedStartDate) : null}
                        onChange={searchUsedStartDate}
                        disablePast={false}
                        numberOfAllowedFutureDays={0}
                        maxDate={usedEndDate ? dayjs(usedEndDate) : null}
                      />
                    </div>
                    <div className="flex flex-row gap-3">
                      <label className="text-gray-600 text-sm self-center">
                        {t('common:and').toLowerCase()}
                      </label>
                      <DateTimePicker
                        useTime={false}
                        value={usedEndDate ? dayjs(usedEndDate) : null}
                        onChange={searchUsedEndDate}
                        disablePast={false}
                        numberOfAllowedFutureDays={0}
                        minDate={usedStartDate ? dayjs(usedStartDate) : null}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <div className="w-[163.5px] text-gray-600 text-sm self-center">
                      {t('common:moderator')}
                      <span className="ml-1.5">:</span>
                    </div>
                    <div>
                      <Select
                        selectClassName="h-[36px] w-[270px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                        options={[
                          {
                            label: t('common:allModerators'),
                            value: 'all',
                          },
                          ...allModeratorsOptions,
                        ]}
                        value={moderatorUserId}
                        onChange={(e) =>
                          filterByModeratorUserId(e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <div className="w-[163.5px] text-gray-600 text-sm self-center">
                      {t('imageOrigin_plural')}
                      <span className="ml-1.5">:</span>
                    </div>
                    <div>
                      <Select
                        selectClassName="h-[36px] w-[270px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                        options={[
                          {
                            label: t('allOrigins'),
                            value: 'all',
                          },
                          {
                            label: t('uploaded_feminine'),
                            value: 'true',
                          },
                          {
                            label: t('automatedReceipt'),
                            value: 'false',
                          },
                        ]}
                        value={isManualUpload}
                        onChange={(e) =>
                          filterByUploadedImagesStatus(e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <div className="w-[163.5px] text-gray-600 text-sm self-center">
                      {t('common:moderationStatus')}
                      <span className="ml-1.5">:</span>
                    </div>
                    <div>
                      <Select
                        selectClassName="h-[36px] w-[270px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                        options={[
                          {
                            label: t('common:allStatuses'),
                            value: 'all',
                          },
                          {
                            label: t('common:toModerate'),
                            value: 'A_MODERER',
                          },
                          {
                            label: t('common:moderated_feminine'),
                            value: 'MODERE',
                          },
                        ]}
                        value={moderationStatus}
                        onChange={(e) =>
                          filterByModerationStatus(e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <div className="w-[163.5px] text-gray-600 text-sm self-center">
                      {t('common:verificationStatus')}
                      <span className="ml-1.5">:</span>
                    </div>
                    <div>
                      <Select
                        selectClassName="h-[36px] w-[270px] bg-gray-FCFCFC dark-mode:bg-coal-500"
                        options={[
                          {
                            label: t('common:allStatuses'),
                            value: 'all',
                          },
                          {
                            label: t('common:toVerify'),
                            value: 'TO_VERIFY',
                          },
                          {
                            label: t('common:verified_feminine'),
                            value: 'VERIFIED',
                          },
                        ]}
                        value={verificationStatus}
                        onChange={(e) =>
                          filterByVerificationStatus(e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </ButtonAdvancedFilter>
            </div>
            <ClearButton
              disabled={filtersEmptyRef.current}
              onClick={clearFilters}
            />
          </div>
        </div>
        <div data-datatable="true" data-datatable-page-size="5">
          <div className="scrollable-x-auto">
            <table
              className="table table-auto table-border"
              data-datatable-table="true"
              id="grid_table"
            >
              <thead>
                <tr>
                  <TableHead
                    text={t('common:image')}
                    thClassName="text-center min-w-[250px]"
                  />
                  <TableHead
                    text={t('common:nbElementsDetected')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbDamagesDetected')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbCorrectionsOfElements')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('common:nbCorrectionsOfDamages')}
                    thClassName="w-[175px]"
                    textClassName="pr-1.5"
                    sortable
                  />
                  <TableHead
                    text={t('imageOrigin')}
                    thClassName="min-w-[140px]"
                    sortable
                  />
                  <TableHead
                    text={t('common:moderation')}
                    thClassName="w-[130px]"
                    textClassName="text-center"
                    sortable
                  />
                  <TableHead thClassName="sticky-action-column" />
                </tr>
              </thead>
              {!isLoading && !isFetching && processings.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={7}>
                      <NoTableDataFound t={t} />
                    </td>
                  </tr>
                </tbody>
              ) : (
                processings.map((item: Processing) => {
                  const isManualUpload = item?.isManualUpload;

                  const isModerated =
                    item?.status === HumanModerationStatus.MODERE;

                  return (
                    <tbody key={item?.id}>
                      <tr className="border-b not-last:border-gray-E5E7EB dark-mode:border-gray-200">
                        <TableCell
                          content={
                            <img
                              className="h-full w-auto object-contain object-center rounded-md m-auto hover:cursor-pointer"
                              alt=""
                              src={item?.imageUrl}
                              onClick={() => {
                                setSelectedProcessing(item);
                                showModal('#engine-correction-preview-modal');
                              }}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.numElementsDetected || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.numDamagesDetected || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.correctedElements || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          content={
                            <CellContentWithRedirect
                              text={`${item?.correctedDamages || 0}`}
                              showActionButton={false}
                            />
                          }
                        />
                        <TableCell
                          tdClassName="text-center"
                          content={
                            <StatusTag
                              text={`${
                                isManualUpload
                                  ? t('uploaded_feminine')
                                  : t('automatedReceipt')
                              }`}
                              className={
                                isManualUpload
                                  ? 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4 text-xs dark-mode:bg-green-EAFFF2/10 dark-mode:border-green-C9F4D2/10 dark-mode:text-green-12C653/75'
                                  : 'bg-purple-DF00FF/5 border-purple-DF00FF/20 text-purple-DF00FF/75 text-xs'
                              }
                            />
                          }
                        />
                        <TableCell
                          tdClassName="text-center"
                          content={
                            <>
                              <StatusTag
                                text={`${
                                  isModerated
                                    ? t('common:moderated_feminine')
                                    : t('common:toModerate')
                                }`}
                                className={
                                  isModerated
                                    ? 'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4 text-xs dark-mode:bg-green-EAFFF2/10 dark-mode:border-green-C9F4D2/10 dark-mode:text-green-12C653/75'
                                    : 'bg-red-FFEEF4 border-red-FFCAD6 text-red-F8275A text-xs dark-mode:bg-red-FFEEF4/10 dark-mode:border-red-FFCAD6/10 dark-mode:text-red-F8275A/80 dark-mode:tracking-wider'
                                }
                              />
                              {item?.status ===
                                HumanModerationStatus.MODERE && (
                                <div className="mt-1.5">
                                  <div
                                    className={twMerge(
                                      'text-[11px] text-primary font-medium uppercase',
                                      !item?.moderationVerifiedByUserId &&
                                        'text-red-FF3680'
                                    )}
                                  >
                                    -&nbsp;
                                    {item?.moderationVerifiedByUserId
                                      ? t('common:verified_feminine')
                                      : t('common:toVerify')}
                                    &nbsp;-
                                  </div>
                                </div>
                              )}
                            </>
                          }
                        />
                        <TableCell
                          tdClassName="sticky-action-column"
                          content={
                            <TableMenu disabled={isLoadingAll}>
                              {isModerated && (
                                // <SubMenuItem
                                //   text={t('alreadyValidated_feminine')}
                                //   itemClassName="hover:!bg-transparent"
                                //   leftIconClassName="mt-0.5 ki-information-2 !text-[19px]"
                                //   disabled
                                // />
                                <SubMenuItem
                                  text={t('common:moderate')}
                                  leftIconClassName="mt-0.5 ki-toggle-on !text-[17.26px]"
                                  onClick={() =>
                                    (window.location.href = `/engine-correction/${item.id}`)
                                  }
                                />
                              )}
                              {!isModerated && (
                                <SubMenuItem
                                  text={t('common:moderate')}
                                  leftIconClassName="mt-0.5 ki-toggle-on !text-[17.26px]"
                                  disabled={
                                    isModerated ||
                                    isPendingDeleteProcessing ||
                                    isPendingDeleteFirestoreImage
                                  }
                                  onClick={() =>
                                    (window.location.href = `/engine-correction/${item.id}`)
                                  }
                                />
                              )}
                              {isManualUpload && !isModerated && (
                                <>
                                  <SubMenuItem
                                    text={t('common:edit')}
                                    leftIconClassName="mt-0.5 ki-notepad-edit !text-[16.7px]"
                                    disabled={
                                      isModerated ||
                                      isPendingDeleteProcessing ||
                                      isPendingDeleteFirestoreImage
                                    }
                                    onClick={() => {
                                      setSelectedProcessing(item);
                                      showModal(
                                        '#engine-correction-upload-modal'
                                      );
                                    }}
                                  />
                                  <SubMenuItem
                                    text={t('common:delete')}
                                    leftIconClassName="ki-trash !text-[19px]"
                                    textClassName="line-clamp-1"
                                    disabled={
                                      isPendingDeleteProcessing ||
                                      isPendingDeleteFirestoreImage
                                    }
                                    onClick={() => {
                                      setSelectedProcessing(item);
                                      showModal(
                                        '#confirmation-remove-item-modal'
                                      );
                                    }}
                                  />
                                </>
                              )}
                            </TableMenu>
                          }
                        />
                      </tr>
                    </tbody>
                  );
                })
              )}
            </table>
          </div>
          <div className="flex flex-row justify-between">
            <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-3 text-gray-600 text-2sm font-medium">
              <div className="flex items-center gap-2">
                {t('common:show')}
                <Select
                  selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500 max-w-26"
                  options={[
                    {
                      label: '5',
                      value: '5',
                    },
                    {
                      label: '10',
                      value: '10',
                    },
                    {
                      label: '20',
                      value: '20',
                    },
                    {
                      label: '50',
                      value: '50',
                    },
                    {
                      label: '100',
                      value: '100',
                    },
                  ]}
                  value={filter.limit.toString()}
                  onChange={(e) =>
                    filterByLimit(Number.parseInt(e.target.value))
                  }
                />
                {t('common:perImage')}
              </div>
              <div className="flex items-center gap-4">
                <span data-datatable-info="true"></span>
                <div
                  className="pagination"
                  data-datatable-pagination="true"
                ></div>
              </div>
            </div>
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </Card>
      <EngineCorrectionUploadModal
        t={t}
        keyCarsData={keyCars?.data}
        defectKeyCarsData={defectKeyCars?.data}
        selectedProcessing={selectedProcessing}
        setSelectedProcessing={setSelectedProcessing}
        onUploadingImages={onUploadingImages}
      />
      <ImagePreviewModal
        id={'engine-correction-preview-modal'}
        bodyClassName="h-max max-h-[93.5vh] min-[1920px]:h-auto"
        isOpen={selectedProcessing !== undefined}
        onClose={() => {
          setSelectedProcessing(undefined);
        }}
        onClickOutsideContent={() => {
          setSelectedProcessing(undefined);
        }}
        onEscapePress={() => {
          setSelectedProcessing(undefined);
        }}
      >
        <img
          className="max-h-screen w-auto object-contain object-center select-none"
          src={selectedProcessing?.imageUrl}
          alt=""
        />
      </ImagePreviewModal>
      <ConfirmationModal
        id="confirmation-remove-item-modal"
        title={t('common:confirmYourAction')}
        description={`${t('areYourSureToDeleteThisImage')} ?`}
        onConfirmLabel={t('common:delete')}
        onConfirm={() => {
          handleDeleteImage();
        }}
      />
      {isLoadingAll && <Loader />}
    </>
  );
};
